import { CameraOutlined } from '@vindr/icons';
import cx from '@vindr/utils/helpers/cx';
import { DragEvent, FC, ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '../Loading';

interface CustomUploadProps {
  accept?: string;
  children?: ReactNode;
  className?: string;
  disabled: boolean;
  loading: boolean;
  multiple?: boolean;
  onFilesSelected: (files: File[]) => void;
  placeholder?: string;
}

const UploadBox: FC<CustomUploadProps> = ({
  accept = 'image/*',
  children,
  className,
  disabled,
  loading,
  multiple,
  onFilesSelected,
  placeholder,
}) => {
  const { t } = useTranslation('Vindoc');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleClick = useCallback(() => {
    if (disabled || loading) return;
    fileInputRef.current?.click();
  }, [disabled, loading]);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const fileArray = Array.from(files);
      onFilesSelected(fileArray);
      fileInputRef.current.value = ''; //? clear the file input
    },
    [onFilesSelected]
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || loading) return;
      setIsDragOver(true);
    },
    [disabled, loading]
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || loading) return;
      setIsDragOver(false);
      const files = e.dataTransfer.files;
      handleFiles(files);
    },
    [disabled, handleFiles, loading]
  );

  return (
    <div
      className={cx(
        'mb-2 mr-2 flex h-[102px] w-[102px] cursor-pointer items-center justify-center border border-dashed border-primary-main bg-[#ffffff0a]',
        isDragOver ? 'border-primary-main bg-[#f0f8ff80]' : 'border-[#647a87] bg-[#ffffff0a]',
        disabled
          ? 'cursor-not-allowed border-[#dc4446]'
          : 'cursor-pointer hover:border-primary-active',
        loading && 'cursor-wait',
        className
      )}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ borderRadius: '2px', transition: 'background-color 0.3s, border-color 0.3s' }}
    >
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
      />
      {loading ? (
        <Loading className="pointer-events-none select-none" />
      ) : children ? (
        children
      ) : (
        <div className="flex flex-col items-center justify-center" style={{ opacity: 0.4 }}>
          <div style={{ marginTop: 8 }}>{t(placeholder || 'Add image')}</div>
          <CameraOutlined style={{ fontSize: 20 }} />
        </div>
      )}
    </div>
  );
};

export default UploadBox;
